/**
 * Product sync, Code tables, Customizer (BLU-1399, CC-1): where the org's
 * PixelWrangler key is saved and the colour codes its print files must use.
 * Collapsed by default; `/product-sync?tab=code#customizer` opens it.
 *
 * Reads (B1, the accepted budget): the section component owns the
 * `getCustomizerColours` query, called BEFORE the role check returns so the
 * hook order never changes, and disabled for a role without
 * `org_settings.read` (no request, nothing rendered). The collapsed header's
 * `N new` chip renders from it (distinct new codes, synced source only), so
 * the Done press's cache patch clears the chip too. The body mounts only while open and adds
 * only `getCustomFiles` (same options as the Custom files step, one cache
 * entry); its colour list reuses the header's cache entry.
 *
 * The two shared pieces (`PwSavedList`, `CustomizerColourCodes`) live in the
 * onboarding feature and import nothing from product-sync (N11).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useRole } from '@/hooks/use-role';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { prefersReducedMotion } from '@/lib/scroll-container';
import { PwSavedList } from '@/features/onboarding/components/custom-files/PwSavedList';
import { CustomizerColourCodes } from '@/features/onboarding/components/custom-files/CustomizerColourCodes';
import { CUSTOMIZER_COLOURS_QUERY_OPTIONS } from '@/features/onboarding/components/custom-files/customizer-query';
import { CUSTOMIZER_SECTION_ID } from '../../lib/tabs';

type CustomizerColoursView = RouterOutputs['onboarding']['getCustomizerColours'];

const TARGET_HASH = `#${CUSTOMIZER_SECTION_ID}`;

export function CustomizerSection() {
  const { t } = useTranslation('productSync');
  const { can } = useRole();
  const allowed = can('org_settings.read');
  // B1: the header owns the colour read; the chip renders from it.
  const colours = trpc.onboarding.getCustomizerColours.useQuery(undefined, {
    ...CUSTOMIZER_COLOURS_QUERY_OPTIONS,
    enabled: allowed,
  });
  const newCount = countNewCodes(colours.data);

  const { hash } = useLocation();
  const [open, setOpen] = useState(hash === TARGET_HASH);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (hash !== TARGET_HASH) return;
    setOpen(true);
    sectionRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }, [hash]);

  if (!allowed) return null;

  return (
    // mt-4: the tab's own gap between its cards (CodeTablesTab's space-y-4).
    <section
      id={CUSTOMIZER_SECTION_ID}
      ref={sectionRef}
      aria-labelledby={`${CUSTOMIZER_SECTION_ID}-title`}
      className="mt-4 scroll-mt-24 rounded-xl border bg-background p-5"
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-start justify-between gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <span className="min-w-0 space-y-1">
              <span className="flex flex-wrap items-center gap-2">
                <span
                  id={`${CUSTOMIZER_SECTION_ID}-title`}
                  className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground/70"
                >
                  {t('customizer.title')}
                </span>
                {newCount > 0 ? (
                  <Badge variant="default" size="sm">
                    {t('customizer.newCount', { count: newCount })}
                  </Badge>
                ) : null}
              </span>
              <span className="block text-sm text-muted-foreground">{t('customizer.summary')}</span>
            </span>
            <ChevronDown
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none',
                !open && '-rotate-90',
              )}
              aria-hidden
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down motion-reduce:animate-none">
          <CustomizerBody />
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

/** Distinct codes among the new rows; 0 unless connected with source `synced`. */
function countNewCodes(view: CustomizerColoursView | undefined): number {
  if (!view?.connected || view.source !== 'synced') return 0;
  return new Set(view.colours.filter((colour) => colour.isNew).map((colour) => colour.code)).size;
}

/** Mounted only while open: adds the where-saved read; the colour list reuses the header's entry. */
function CustomizerBody() {
  const { t } = useTranslation('productSync');
  const customFiles = trpc.onboarding.getCustomFiles.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-5 pt-4">
      {customFiles.isLoading ? (
        <Skeleton className="h-4 w-1/2" />
      ) : customFiles.isError || !customFiles.data ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-muted-foreground">{t('customizer.statusError')}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void customFiles.refetch()}>
            {t('states.retry')}
          </Button>
        </div>
      ) : (
        <PwSavedList view={customFiles.data} />
      )}
      <CustomizerColourCodes />
    </div>
  );
}
